import React, { useLayoutEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { CircularProgress, IconButton, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { CloseRounded } from "@mui-symbols-material/w300";
import { BuschGroupLogo } from "@dreebit-registry/vsm-microservice-components";
import AssetLocationsMap from "./AssetLocationsMap";
import { useAssetMapFullscreen } from "./useAssetMapFullscreen";

/** Width the app header draws its logo at. */
const LOGO_WIDTH = 200;
/** Where the header's logo sits with the app switcher in front of it (header padding 4 + switcher 44 + logo padding 16). */
const FALLBACK_LOGO_POSITION = { left: 64, top: 22, height: 19 };

/**
 * Viewport position of the app header's logo. The header stays mounted under the
 * full-screen dialog, so its logo is measured instead of re-deriving the header layout
 * (the app switcher in front of it is only shown with access to several apps). Falls
 * back to the usual position when the header hides its logo (narrow screens).
 */
const useHeaderLogoPosition = (active: boolean) => {
  const [position, setPosition] = useState(FALLBACK_LOGO_POSITION);

  useLayoutEffect(() => {
    if (!active) return;
    const measure = () => {
      const rect = document.querySelector(".header-logo-wrapper svg")?.getBoundingClientRect();
      setPosition(rect && rect.width > 0
        ? { left: rect.left, top: rect.top, height: rect.height }
        : FALLBACK_LOGO_POSITION);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [active]);

  return position;
};

/**
 * The asset-locations map filling the whole window (`/dashboard?map=fullscreen`), above
 * the app header, with the header's logo at its usual place and the asset count below it.
 */
const AssetMapFullscreen: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, viewBounds, close } = useAssetMapFullscreen();
  const logo = useHeaderLogoPosition(isOpen);
  const logoCenterY = logo.top + logo.height / 2;

  return (
    <Dialog fullScreen open={isOpen} onClose={close}>
      {isOpen && (
        <AssetLocationsMap
          initialViewBounds={viewBounds}
          renderOverlay={({ assetCount, loading }) => (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 1001,
                pointerEvents: "none",
              }}
            >
              {/* Light fade behind logo and close button so both stay legible over clusters. */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: logo.top + logo.height + 64,
                  background: "linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0))",
                }}
              />
              <div style={{ position: "absolute", left: logo.left, top: logo.top, pointerEvents: "auto" }}>
                <div onClick={close} style={{ cursor: "pointer", lineHeight: 0 }}>
                  <BuschGroupLogo width={LOGO_WIDTH} color={"#333"} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                  <Typography
                    level="titleSmallLight"
                    sx={{ color: "#333", textShadow: "0 0 4px rgba(255,255,255,0.8)" }}
                  >
                    {t("Asset Locations")}{assetCount != null ? ` (${assetCount})` : ""}
                  </Typography>
                  {loading && <CircularProgress size="sm" />}
                </div>
              </div>
              <IconButton
                aria-label={t("Close")}
                onClick={close}
                variant="soft"
                sx={{
                  position: "absolute",
                  top: logoCenterY,
                  right: 16,
                  transform: "translateY(-50%)",
                  pointerEvents: "auto",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                }}
              >
                <CloseRounded />
              </IconButton>
            </div>
          )}
        />
      )}
    </Dialog>
  );
};

export default AssetMapFullscreen;
